import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { requestNewSupplier } from './requestNewSupplier';
import { type RequestNewSupplierMutationOptions } from './types';

/**
 * React Query based custom hook for requesting a new supplier that is not on the VESTIGAS network.
 *
 * Invalidates the supplier overview and supplier requests queries on settled.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/post_unlisted_supplier_suppliers_requests_post
 */
export const useMutationRequestNewSupplier = (
  options?: RequestNewSupplierMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => requestNewSupplier(payload),
    onSettled(_data, _error, variables) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getOverview(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getRequests(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysSupplier.getPriority(variables.recipientCompanyId),
      });
    },
    throwOnError: false,
    ...options,
  });
};
